import { useState, useEffect } from 'react';
import { Database, ReviewResult } from '../types/database';
import { DataLayer } from '../lib/dataLayer';
import { useAuth } from '../contexts/AuthContext';
import { CardDisplay } from './CardDisplay';
import { useStudyKeyboard, commonStudyKeys } from '../hooks/useStudyKeyboard';

type Card = Database['public']['Tables']['cards']['Row'];
type UserCardProgress = Database['public']['Tables']['user_card_progress']['Row'];

interface SequentialStudyModeProps {
  selectedTags: string[];
  selectedExercise: string;
  onExit: () => void;
  // Optional source-based study
  sourceId?: string;
  sourceName?: string;
}

export function SequentialStudyMode({ selectedTags, selectedExercise, onExit, sourceId, sourceName }: SequentialStudyModeProps) {
  const { user } = useAuth();
  const [cards, setCards] = useState<Card[]>([]);
  const [currentPosition, setCurrentPosition] = useState(0);
  const [showBack, setShowBack] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasOrder, setHasOrder] = useState(false);
  const [showJumpDialog, setShowJumpDialog] = useState(false);
  const [jumpToPosition, setJumpToPosition] = useState('');
  const [sessionStats, setSessionStats] = useState({
    cardsStudied: new Set<string>(),
    cardsRecalled: 0,
    cardsMissed: 0,
    cardsMastered: 0
  });

  const currentCard = cards[currentPosition];
  const totalActiveCards = cards.length;

  // Load cards and restore position
  useEffect(() => {
    const loadCards = async () => {
      if (!user) return;

      setIsLoading(true);
      try {
        let fetchedCards: Card[];
        let hasOrderField: boolean;
        let positionKey: string[];
        const filters = selectedTags.length > 0
          ? selectedTags
          : selectedExercise
            ? [selectedExercise]
            : [];

        if (sourceId) {
          // Load cards by source
          fetchedCards = await DataLayer.getCardsBySource(sourceId);
          // Filter out mastered/hidden cards
          const progress = await DataLayer.getUserCardProgress(user.id);
          const progressMap = new Map(progress.map(p => [p.card_id, p]));
          fetchedCards = fetchedCards.filter(card => {
            const cardProgress = progressMap.get(card.id);
            return !cardProgress || (cardProgress.status !== 'mastered' && cardProgress.status !== 'hidden');
          });
          hasOrderField = fetchedCards.some(card => card.order !== null);
          positionKey = [`source:${sourceId}`];
        } else {
          // Load cards by selected tags and/or exercises
          const result = await DataLayer.getCardsForSequentialStudy(filters, user.id);
          fetchedCards = result.cards;
          hasOrderField = result.hasOrder;
          positionKey = filters;
        }

        if (fetchedCards.length === 0) {
          alert('No active cards found for sequential study');
          onExit();
          return;
        }

        setCards(fetchedCards);
        setHasOrder(hasOrderField);

        // Restore saved position
        const savedPosition = await DataLayer.getSequentialStudyPosition(user.id, positionKey);
        setCurrentPosition(Math.min(savedPosition, fetchedCards.length - 1));
      } catch (error) {
        console.error('Error loading cards:', error);
        alert('Error loading cards');
        onExit();
      } finally {
        setIsLoading(false);
      }
    };

    loadCards();
  }, [user, selectedTags, selectedExercise, sourceId, onExit]);

  // Save position when it changes
  useEffect(() => {
    if (!user || cards.length === 0) return;

    const filters = selectedTags.length > 0
      ? selectedTags
      : selectedExercise
        ? [selectedExercise]
        : [];
    const positionKey = sourceId
      ? [`source:${sourceId}`]
      : filters;
    DataLayer.saveSequentialStudyPosition(user.id, positionKey, currentPosition);
  }, [currentPosition, user, selectedTags, selectedExercise, sourceId, cards.length]);

  const handleReviewResult = async (result: ReviewResult) => {
    if (!user || !currentCard) return;

    // Update session stats
    const newStats = { ...sessionStats };
    newStats.cardsStudied.add(currentCard.id);
    
    if (result === 'recalled') {
      newStats.cardsRecalled++;
    } else if (result === 'missed') {
      newStats.cardsMissed++;
    }

    // Get current progress
    const allProgress = await DataLayer.getUserCardProgress(user.id);
    const currentProgress = allProgress.find(p => p.card_id === currentCard.id);
    
    let newMastery = currentProgress?.mastery_score || 0;
    let newStatus: 'active' | 'mastered' | 'hidden' = currentProgress?.status || 'active';

    // Update mastery score
    if (result === 'recalled') {
      newMastery = Math.min(100, newMastery + 20);
      if (newMastery >= 100) {
        newStatus = 'mastered';
        newStats.cardsMastered++;
      }
    } else if (result === 'missed') {
      newMastery = Math.max(0, newMastery - 10);
      newStatus = 'active';
    } else if (result === 'hidden') {
      newStatus = 'hidden';
    }

    // Save progress
    const progressUpdate: UserCardProgress = {
      user_id: user.id,
      card_id: currentCard.id,
      mastery_score: newMastery,
      status: newStatus,
      last_reviewed: new Date().toISOString(),
      review_count: (currentProgress?.review_count || 0) + 1,
      created_at: currentProgress?.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    await DataLayer.upsertUserCardProgress(progressUpdate);
    setSessionStats(newStats);

    // If card was mastered or hidden, remove it from the current session
    if (newStatus === 'mastered' || newStatus === 'hidden') {
      const newCards = cards.filter(c => c.id !== currentCard.id);
      if (newCards.length === 0) {
        alert('All cards completed!');
        onExit();
        return;
      }
      setCards(newCards);
      // Adjust position if needed
      if (currentPosition >= newCards.length) {
        setCurrentPosition(0);
      }
    } else {
      // Move to next card
      moveToNextCard();
    }
    
    setShowBack(false);
  };

  const moveToNextCard = () => {
    if (cards.length === 0) return;
    
    // Loop back to beginning after last card
    const nextPosition = (currentPosition + 1) % cards.length;
    setCurrentPosition(nextPosition);
    setShowBack(false);
  };

  const moveToPreviousCard = () => {
    if (cards.length === 0) return;
    
    // Loop to end when going back from first card
    const prevPosition = currentPosition === 0 ? cards.length - 1 : currentPosition - 1;
    setCurrentPosition(prevPosition);
    setShowBack(false);
  };

  const handleJumpToPosition = () => {
    const position = parseInt(jumpToPosition, 10);
    if (isNaN(position) || position < 1 || position > cards.length) {
      alert(`Please enter a number between 1 and ${cards.length}`);
      return;
    }
    setCurrentPosition(position - 1); // Convert to 0-based index
    setShowJumpDialog(false);
    setJumpToPosition('');
    setShowBack(false);
  };

  // Keyboard shortcuts via shared hook
  useStudyKeyboard({
    enabled: true,
    showBack,
    skipWhen: showJumpDialog,
    keyMappings: [
      {
        keys: [' ', 'enter'],
        preventDefault: true,
        handler: () => {
          if (!showBack) {
            setShowBack(true);
          } else {
            handleReviewResult('recalled');
          }
        },
      },
      {
        keys: ['arrowleft'],
        preventDefault: true,
        handler: () => moveToPreviousCard(),
      },
      {
        keys: ['arrowright'],
        preventDefault: true,
        handler: () => {
          if (showBack) {
            handleReviewResult('missed');
          } else {
            setShowBack(true);
          }
        },
      },
      { keys: ['h'], handler: () => handleReviewResult('hidden'), requiresShowBack: true },
      { keys: ['j'], handler: () => setShowJumpDialog(true) },
      commonStudyKeys.toggleAudio(),
      {
        keys: ['escape'],
        handler: () => {
          if (showJumpDialog) {
            setShowJumpDialog(false);
            setJumpToPosition('');
          }
        },
      },
    ],
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl">Loading cards...</div>
      </div>
    );
  }

  if (!currentCard) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl">No cards available</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <h2 className="text-xl font-semibold">
                {sourceName ? sourceName : 'Sequential Study Mode'}
              </h2>
              <span className="text-sm text-gray-500">
                Card {currentPosition + 1} of {totalActiveCards}
                {hasOrder && currentCard.order !== null && ` (Order: ${currentCard.order})`}
              </span>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-600">
                Studied: {sessionStats.cardsStudied.size} | 
                Recalled: {sessionStats.cardsRecalled} | 
                Missed: {sessionStats.cardsMissed} | 
                Mastered: {sessionStats.cardsMastered}
              </div>
              <button
                onClick={onExit}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Exit
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-gray-200 h-1">
        <div 
          className="bg-blue-600 h-1 transition-all duration-300"
          style={{ width: `${((currentPosition + 1) / totalActiveCards) * 100}%` }}
        />
      </div>

      {/* Card display */}
      <div className="flex-1 p-8">
        <div className="max-w-2xl mx-auto">
          <CardDisplay
            card={currentCard}
            showBack={showBack}
            onShowBack={() => setShowBack(true)}
            onAction={handleReviewResult}
            userId={user?.id}
            currentMastery={0}
            showMasteryVisualization={false}
            actionButtons="sequential"
          />

          {/* Navigation controls */}
          <div className="mt-6 flex justify-between items-center">
            <button
              onClick={moveToPreviousCard}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              ← Previous
            </button>
            
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  setCurrentPosition(0);
                  setShowBack(false);
                              }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Start Over
              </button>
              <button
                onClick={() => setShowJumpDialog(true)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Jump To... (J)
              </button>
            </div>
            
            <button
              onClick={moveToNextCard}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Skip →
            </button>
          </div>
        </div>
      </div>

      {/* Jump dialog */}
      {showJumpDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full">
            <h3 className="text-lg font-medium mb-4">Jump to Card</h3>
            <input
              type="number"
              min="1"
              max={cards.length}
              value={jumpToPosition}
              onChange={(e) => setJumpToPosition(e.target.value)}
              placeholder={`Enter 1-${cards.length}`}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
            <div className="mt-4 flex justify-end space-x-2">
              <button
                onClick={() => {
                  setShowJumpDialog(false);
                  setJumpToPosition('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleJumpToPosition}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Jump
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}